import { Badge, Box, Tooltip } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { CONVERSATIONS } from 'utils/config/constants/controllerNames';
import { onUpdateDoc } from 'utils/config/firebase';

import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Star, StarOutlineOutlined, Visibility, VisibilityOff } from '@mui/icons-material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { FC } from 'react';
import { TopActionsType } from '../../types';
import { getLocalTime } from 'helpers/dates';

const TopActions: FC<TopActionsType> = ({
    version,
    language,
    created_at,
    hasActionBtns,
    messagesLength = 0,
    isHidden,
    isBookmarked,
    onCloseParentFullScreen,
    messages,
    messageId,
    hiddenMessages,
    document_id,
    isFullScreenMode,
    onOpenModal,
    isGeneratingForThis,
    group,
    isFirstGroupItem,
    groupBookmarksCount,
    showBiasTrigger,
    onShowBiasTrigger,
}) => {
    const { t } = useTranslation();

    const toggleIsHidden = (document_id: string, isHiddenCurrentValue: boolean) => {
        if (onCloseParentFullScreen) {
            onCloseParentFullScreen();
        }
        if (isHidden || messagesLength > 1) {
            const newValue = !isHiddenCurrentValue;
            const updatedMessages = messages?.map((item) => {
                if (item.message_id === messageId) {
                    return { ...item, isHidden: newValue };
                }
                return item;
            });

            const hiddenMessagesIds = newValue
                ? [...hiddenMessages, messageId]
                : hiddenMessages.filter((m) => m !== messageId);

            onUpdateDoc(CONVERSATIONS, document_id, {
                messages: updatedMessages,
                hiddenMessages: hiddenMessagesIds,
            });
        }
    };
    const toggleIsBookmarked = (document_id: string, isBookmarkedCurrentValue: boolean) => {
        const newValue = !isBookmarkedCurrentValue;
        const updatedMessages = messages?.map((item) => {
            if (item.message_id === messageId) {
                return { ...item, isBookmarked: newValue };
            }
            return item;
        });

        onUpdateDoc(CONVERSATIONS, document_id, {
            messages: updatedMessages,
        });
    };
    const showBookmark =
        (!isFullScreenMode && !group?.isCollapsed && !isHidden) ||
        (group?.isCollapsed && isFirstGroupItem);
    const bookmarkCount =
        isFirstGroupItem && isBookmarked && groupBookmarksCount
            ? groupBookmarksCount - 1
            : groupBookmarksCount;
    const bookmarkElement = isBookmarked ? (
        <Star className="bookmark-icon" />
    ) : (
        <StarOutlineOutlined className="bookmark-outlined-icon" />
    );

    return (
        <Box className="editor-title" sx={{ background: group?.color ?? '#ecf3fc' }}>
            <Box className="editor-title-version">
                <Box className="editor-title-value">
                    {`${t('web_app.home.output.version')} ${version.major}${
                        version.minor ? '.' + version.minor : ''
                    }_${language?.toUpperCase()}`}
                </Box>
                {created_at && (
                    <Box className="editor-title-date">
                        {moment(getLocalTime(created_at)).format('lll')}
                    </Box>
                )}
            </Box>
            {showBookmark && (
                <button
                    className="bookmark-container"
                    onClick={() => toggleIsBookmarked(document_id, !!isBookmarked)}
                >
                    {group?.isCollapsed && bookmarkCount !== 0 ? (
                        <>
                            <Badge
                                className="bookmark-count-badge"
                                badgeContent={'+' + bookmarkCount}
                            >
                                {bookmarkElement}
                            </Badge>
                        </>
                    ) : (
                        bookmarkElement
                    )}
                </button>
            )}
            {hasActionBtns && (
                <Box className="actions">
                    {showBiasTrigger && (
                        <Tooltip placement="top" title={t('web_app.bias_checker.trigger_tooltip')}>
                            <button
                                aria-label="Bias checker warning"
                                className="bias-overlay-trigger-btn"
                                onClick={onShowBiasTrigger}
                            >
                                <WarningAmberRoundedIcon sx={{ fontSize: '21px !important ' }} />
                            </button>
                        </Tooltip>
                    )}
                    {(messagesLength > 1 || isHidden) && !group?.isCollapsed && (
                        <button onClick={() => toggleIsHidden(document_id, !!isHidden)}>
                            {isHidden ? (
                                <Tooltip placement="top" title={t('web_app.tooltips.eye_show')}>
                                    <Visibility />
                                </Tooltip>
                            ) : (
                                <Tooltip placement="top" title={t('web_app.tooltips.eye_hide')}>
                                    <VisibilityOff />
                                </Tooltip>
                            )}
                        </button>
                    )}
                    <Tooltip placement="top" title={t('web_app.tooltips.full_screen')}>
                        {isFullScreenMode ? (
                            <button onClick={onCloseParentFullScreen}>
                                <CloseFullscreenIcon />
                            </button>
                        ) : (
                            <span className="actions-fullscreen">
                                <button onClick={onOpenModal} disabled={isGeneratingForThis}>
                                    <OpenInFullIcon />
                                </button>
                            </span>
                        )}
                    </Tooltip>
                </Box>
            )}
        </Box>
    );
};

export default TopActions;
